import { AxiosInstance, AxiosResponse } from "axios";
import { CustomInternalAxiosRequestConfig } from "../../../utils/Types";
import { getAuthValue } from "../hooks/useAuthValue";
import { onlineManager } from "@tanstack/react-query";
import refreshAccessToken from "../hooks/useRefreshAccessTokenMutation";
import { REFRESH_TOKEN_BEFORE_TIME } from "../../../utils/Constants";
import { enableApiHeaders } from "../../../utils/Axios";

const cache: any = {
  refreshCall: undefined,
  forceRefresh: false,
};

export const getAuthToken = async () => {
  if (cache.forceRefresh) {
    return cache.refreshCall;
  }
  const { access_token, expires_in = 0, isLoggedIn } = getAuthValue();

  if (!isLoggedIn) {
    return access_token;
  }

  if (!onlineManager.isOnline()) {
    return access_token;
  }
  const now = Date.now();
  const remainingTime = expires_in - now;

  if (remainingTime < REFRESH_TOKEN_BEFORE_TIME) {
    if (!cache.refreshCall) {
      cache.refreshCall = refreshAccessToken().finally(() => {
        cache.refreshCall = undefined;
      });
    }
  }
  if (remainingTime >= REFRESH_TOKEN_BEFORE_TIME) {
    return access_token;
  }
  return cache.refreshCall;
};

export const forceRefreshToken = async () => {
  const { isLoggedIn } = getAuthValue();
  if (!isLoggedIn) return;

  if (!cache.refreshCall) {
    cache.refreshCall = refreshAccessToken().finally(() => {
      cache.refreshCall = undefined;
      cache.forceRefresh = false;
    });
    cache.forceRefresh = true;
  }

  return cache.refreshCall;
};

export function createAddTokenInterceptor(instance: AxiosInstance) {
  return instance.interceptors.request.use(
    async (config: CustomInternalAxiosRequestConfig) => {
      if (config?.authRequired) {
        const access_token = await getAuthToken();
        config.headers.Authorization = `Bearer ${access_token}`;
      } else {
        delete config.headers.Authorization;
      }
      return config;
    }
  );
}

export const createAuthRefreshTokenInterceptor = (instance: AxiosInstance) => {
  return instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: any) => {
      const originalRequest = error.config;
      const { isLoggedIn } = getAuthValue();

      if (
        error?.response?.status === 401 &&
        !originalRequest?.skipAuthRefresh &&
        isLoggedIn
      ) {
        return forceRefreshToken()
          .catch((err) => Promise.reject(new Error(err)))
          .then(() => {
            error.config.skipAuthRefresh = true;
            enableApiHeaders(error.config.headers);
            return instance.request(error.config);
          });
      } else if (error?.response) {
        const data = JSON.stringify(error?.response?.data ?? {});
        error.message = error.message + `\n ${data}`;
      }

      return Promise.reject(new Error(error));
    }
  );
};
